package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SupervisionCheck
import com.evoapps.evoshift.domain.model.WorkSpan
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.LocalDate
import kotlin.math.abs
import kotlin.math.min
import kotlin.math.roundToInt

/**
 * Fills a roster's open shifts automatically (SH-44), following the owner's own rules from the
 * spreadsheet era (where Gemini filled the month and a script evened out the hours). Pure and
 * deterministic: the same [ScheduleProblem] always gives the same [SchedulePlan]; ties between
 * equally good people are broken by a hash of [ScheduleOptions.seed], the person and the shift.
 *
 * ## Hard rules: never broken
 * A person can take a shift only if all of these hold (the first one that fails is the reason
 * shown in [UnfilledShift.rejections]):
 * - The shift's role and skills match theirs; their monthly target is not 0 hours.
 * - No approved absence that day.
 * - Their medical exam and OHS training still cover that day (SH-47,
 *   [StaffProfile.staffChecksValidThrough]).
 * - **Rule 1** – they said they are available that day. "Not available" (their "ND") always
 *   excludes the day; a day without any answer excludes it too while
 *   [ScheduleOptions.requireSubmittedAvailability] is on (the default: "never use people with
 *   empty availability"). SH-221: in the workplace's first roster ([ScheduleOptions.firstRoster])
 *   someone who sent no availability at all counts as available on every day.
 * - Their preferred slot of the day, when [PreferenceMode.STRICT] (the default, as their sheet
 *   read "Rano"/"Międzyzmiana"/"Popołudnie"): the shift must fit it ([SlotMatcher]).
 * - **Rule 9** – their personal rules ([StaffConstraint]: max days a week, only mornings, ...).
 * - No overlap with any of their shifts, here or at another workplace; one shift a day.
 * - **Rule 8** – at least [ScheduleOptions.minRestHours] (default 15) between the end of one
 *   shift and the start of the next, never less than the legal 11 hours, counting the previous
 *   and next month and other workplaces.
 * - **Rule 3** – at most [ScheduleOptions.maxConsecutiveDays] (default 6) working days in a row,
 *   other months and workplaces included: nobody works 7 days in a row.
 * - **SH-112, the labour code** ([LabourCode], the same rules the manual roster checks warn
 *   about in [LabourCodeCheck]): 11 hours of rest in each 24 hours from a shift's start, 35
 *   uninterrupted hours in each week; for an employment contract also no day above the daily norm,
 *   at most 48 hours a week on average in the month, and overtime within the yearly cap's share.
 *   SH-133: the rest rules bind employees always and other contracts while
 *   [ScheduleOptions.restRulesForAllContracts] is on ([LabourCode.restRulesApply]); the owner or a
 *   partner without a contract keeps only rule 8.
 * - **SH-133, a lead every day** ([ScheduleOptions.requireLeadEachDay]): someone who is not a
 *   lead never works a day nobody leads, like the roster's "no shift lead" day warning; on a day
 *   no lead can work at all the shifts are filled anyway and the plan reports it
 *   ([WarningKind.DAYS_WITHOUT_LEAD]).
 * - Their monthly target (declared hours, or FTE × the month's working hours for an employment
 *   contract with a salary) is not exceeded (plus [ScheduleOptions.maxHoursOverTarget]).
 * - **Rule 5** – every block (shifts of a day that overlap substantially, see
 *   [ScheduleOptions.blockMinOverlap]) of two or more shifts has a shift lead, and once two
 *   people work in it, someone who is not a lead. A block is never started with someone who
 *   is not a lead, so it is never left without one.
 * - **Rule 7** – at no moment is everyone at work someone who needs supervision.
 * Assignments that already exist are kept as they are (unless [ScheduleOptions.replan]); the
 * plan only adds to them and never makes them break a rule they did not already break.
 *
 * ## The order: who gets what
 * 1. **Rule 6 – the owner's mornings.** For every day, the owner is given the earliest open
 *    shift starting before [ScheduleOptions.morningEndsAt] that they can take.
 * 2. **Rule 5 – leads first.** Each block without a shift lead gets one, starting with the block
 *    that has the fewest possible leads; then each block that has only leads gets someone who is
 *    not one. Leads are scarce, so they are placed before anyone else uses up their hours.
 * 3. **Everything else, hardest first.** Repeatedly, the open shift with the fewest people who
 *    can take it is filled with the best of them, until no open shift can be filled.
 * 4. **Balancing** (their `optymalizujGrafikAlgorytmem`), for civil-law contracts (umowa
 *    zlecenie) with a known target only: while the highest and lowest hours/target ratios differ
 *    by [ScheduleOptions.balanceTolerance] (0.1) or more, a shift the plan gave the person with
 *    the highest ratio moves to the one with the lowest (or the next pair that can swap), if the
 *    receiver passes every hard rule, the shift fits their preferred slot, and the gap between
 *    the two shrinks. It stops when no such move exists or after
 *    [ScheduleOptions.balanceMaxIterations] moves. Only shifts this plan assigned are moved.
 *
 * ## The best candidate (soft rules)
 * Among the people who pass every hard rule, the one with the highest score gets the shift. The
 * score adds up ([ScheduleWeights]):
 * - rule 5: a lead where the block has none, someone who is not a lead where it has only leads,
 *   and a small cost for a second lead in a block;
 * - rule 6: the owner on a morning shift (and a cost for a later one);
 * - **rule 2** – scarce but committed people first: people are ranked once by the hours they
 *   could work in the range (on each day they are available, the longest shift they could take),
 *   fewest first; equal ones by their ratio of available hours to the hours they still need,
 *   highest first. The first in this order gets the full [ScheduleWeights.scarcity], the last none:
 *   their few days are the only chance to give them hours, while flexible people fit anywhere;
 * - how far below their target they are (their hours / target, or / full time when unknown);
 * - **rule 4** – the day joins their run of working days and the run stays within 2–5 days
 *   (longer runs cost per extra day);
 * - **rule 3** – they have been off for more than [ScheduleOptions.maxIdleDays] days;
 * - the shift is in their preferred slot of the day;
 * - a tiny seeded tie-break.
 *
 * Every assignment carries the reasons that applied ([AssignmentReason]); every shift left
 * empty carries why each person could not take it ([UnfilledShift]).
 *
 * ## Outside proposals (SH-90)
 * [ScheduleProblem.proposals] (the AI's suggestions, mapped back from aliases) are placed before
 * step 1, each only if it passes every hard rule above with the same checks the rules use. They
 * are tried again while any of them gets placed (a block's lead may make its other proposals
 * valid); what still breaks a rule, names an unknown person, a shift that is not open or one
 * already proposed is dropped with the reason ([SchedulePlan.dropped]). The steps then fill the
 * remaining open shifts; balancing never moves an accepted proposal.
 */
object AutoScheduler {
    fun plan(problem: ScheduleProblem): SchedulePlan = Solver(problem).solve()
}

private const val WINDOW_MARGIN_DAYS = 40
private const val UNASSIGNED = -1
/** A kept assignment of someone who is not on the team (left, inactive): present, nothing else known. */
private const val UNKNOWN_PERSON = -2

private class Rejection(val reason: ScheduleReason, val constraint: StaffConstraint? = null)

private class Interval(val start: Long, val end: Long, val day: Int, val here: Boolean, val slot: Int)

private class Person(val index: Int, val profile: StaffProfile, windowDays: Int) {
    val lead = profile.leadsShifts || profile.isOwner
    val supervised = profile.needsSupervision && !lead
    val owner = profile.isOwner
    /** SH-112: the labour code's hours limits apply (an employment contract). */
    val hoursRules = LabourCode.hoursRulesApply(profile.contract)
    /** SH-133: the 11-hour and 35-hour rest apply (set up with the options). */
    var restRules = true
    val intervals = ArrayList<Interval>()
    /** Shifts per window day, anywhere / here, and minutes here. */
    val dayAll = IntArray(windowDays)
    val dayHere = IntArray(windowDays)
    val minutesHere = IntArray(windowDays)
    var monthMinutes = 0
    var monthMinutesBefore = 0
    var targetMinutes: Double? = null
    var capMinutes: Int? = null
    var referenceMinutes = 160.0 * 60
    var restMinutes = 0L
    var maxConsecutive = 0
    var dynamicConstraints: List<StaffConstraint> = emptyList()
    var staticConstraints: List<StaffConstraint> = emptyList()
    var availableMinutes = 0
    var commitment = 0.0
    var rank = 0
    val id: String get() = profile.userId
}

private class Slot(
    val index: Int,
    val shift: Shift,
    val start: Long,
    val end: Long,
    val dayEpoch: Long,
    /** Window index of the shift's date. */
    val day: Int,
    val startOfDay: Int,
    val endOfDay: Int,
    val toFill: Boolean,
    /** Person index who had it when planning started, [UNASSIGNED] or [UNKNOWN_PERSON]. */
    val initialAssignee: Int
) {
    val minutes: Int = (end - start).toInt()
    var assignee = UNASSIGNED
    var crew = 0
    var cluster = 0
    var reasons: List<AssignmentReason> = emptyList()
    var balancedFrom: Int = UNASSIGNED
    /** SH-90: placed from an outside proposal (never moved by balancing). */
    var proposed = false
}

private class Group(val slots: IntArray) {
    var fillSlots: IntArray = IntArray(0)
    var assigned = 0
    var leads = 0
    var nonLeads = 0
    var open = 0
}

private class Solver(private val problem: ScheduleProblem) {
    private val o = problem.options
    private val w = o.weights
    private val rangeStart = problem.range.start.toEpochDays()
    private val rangeEnd = problem.range.endInclusive.toEpochDays()
    private val base = rangeStart - WINDOW_MARGIN_DAYS
    private val windowDays = (rangeEnd - rangeStart + 1 + 2 * WINDOW_MARGIN_DAYS).toInt()
    private val monthStartEpoch = LocalDate(problem.year, problem.month, 1).toEpochDays()
    private val monthEndEpoch = LocalDate(
        if (problem.month == 12) problem.year + 1 else problem.year, if (problem.month == 12) 1 else problem.month + 1, 1
    ).toEpochDays() - 1
    /** Window indices of the roster's month, clipped to the window. */
    private val monthDays: IntRange get() =
        maxOf(0, (monthStartEpoch - base).toInt())..minOf(windowDays - 1, (monthEndEpoch - base).toInt())
    private val monthPrefix = "${problem.year}-${problem.month.toString().padStart(2, '0')}"
    private val morningEnd = clockMinutes(o.morningEndsAt)
    private val slotsById = problem.availabilitySlots.associateBy { it.id }

    private val people: List<Person> = problem.staff.sortedBy { it.userId }.mapIndexed { i, p -> Person(i, p, windowDays) }
    private val personIndex = people.associate { it.id to it.index }
    private val n = people.size

    private val slots: List<Slot>
    private val fillSlots: List<Slot>
    private val crews = ArrayList<Group>()
    private val clusters = ArrayList<Group>()

    private val staticRejections: Array<Rejection?>
    /** Per slot and person: 1 the shift is in their preferred slot, -1 it is not, 0 no preference. */
    private val preferenceFit: ByteArray
    /** [check] results of the open shifts, recomputed when [invalidate]d. */
    private val cache: Array<Rejection?>
    private val cacheValid: BooleanArray
    private val simple = ScheduleReason.entries.associateWith { Rejection(it) }
    private val dropped = ArrayList<DroppedProposal>()
    /** SH-133: leads working each roster day here (ISO date), for [ScheduleOptions.requireLeadEachDay]. */
    private val leadsByDate = HashMap<String, Int>()
    /** SH-133: days a lead works already or could take an open shift (else the day may go without). */
    private val leadPossible = HashSet<String>()

    init {
        val parsed = problem.shifts
            .sortedWith(compareBy<Shift>({ it.plannedTime.start }, { it.plannedTime.end }, { it.id }))
            .mapNotNull { shift -> parseTimes(shift)?.let { shift to it } }
        slots = parsed.mapIndexed { i, (shift, times) ->
            val (start, end, dayEpoch) = times
            val inRange = shift.date in problem.range
            val clocked = shift.actualTime?.start?.isNotEmpty() == true
            val initial = shift.userId?.let { personIndex[it] ?: UNKNOWN_PERSON } ?: UNASSIGNED
            val toFill = inRange && (shift.userId == null || (o.replan && !clocked))
            Slot(
                index = i, shift = shift, start = start, end = end, dayEpoch = dayEpoch,
                day = (dayEpoch - base).toInt(),
                startOfDay = (start - dayEpoch * 1440).toInt(), endOfDay = (end - dayEpoch * 1440).toInt(),
                toFill = toFill, initialAssignee = initial
            )
        }
        fillSlots = slots.filter { it.toFill }
        setUpPeople()
        buildGroups()
        // Kept assignments, then everyone's shifts elsewhere.
        slots.filter { !it.toFill && it.initialAssignee != UNASSIGNED }.forEach { occupy(it, it.initialAssignee) }
        people.forEach { it.monthMinutesBefore = it.monthMinutes }
        problem.contextShifts.forEach { context ->
            val p = personIndex[context.userId]?.let { people[it] } ?: return@forEach
            val start = isoMinutes(context.plannedTime.start) ?: return@forEach
            val end = isoMinutes(context.plannedTime.end) ?: return@forEach
            if (end <= start) return@forEach
            val day = (epochDay(context.date) - base).toInt()
            addInterval(p, Interval(start, end, day, context.atThisWorkplace, -1))
            if (day in 0 until windowDays) {
                p.dayAll[day]++
                if (context.atThisWorkplace) {
                    p.dayHere[day]++
                    p.minutesHere[day] += (end - start).toInt()
                }
            }
        }
        staticRejections = arrayOfNulls(slots.size * n)
        preferenceFit = ByteArray(slots.size * n)
        cache = arrayOfNulls(slots.size * n)
        cacheValid = BooleanArray(slots.size * n)
        computeStatic()
        rankPeople()
        if (o.requireLeadEachDay) {
            leadsByDate.filterValues { it > 0 }.keys.forEach { leadPossible += it }
            fillSlots.forEach { s -> if (people.any { it.lead && staticRejections[s.index * n + it.index] == null }) leadPossible += s.shift.date }
        }
    }

    fun solve(): SchedulePlan {
        if (problem.proposals.isNotEmpty()) placeProposals()
        if (o.ownerTakesMornings) ownerMornings()
        if (o.requireShiftLead) crewPhase(lead = true)
        if (o.requireNonLead) crewPhase(lead = false)
        fill()
        val balancing = balance()
        return result(balancing)
    }

    // --- Setup ----------------------------------------------------------------------------------

    private fun setUpPeople() {
        val knownTargets = people.mapNotNull { it.profile.target.hours?.takeIf { h -> h > 0 } }.sorted()
        val reference = if (knownTargets.isEmpty()) problem.nominalMonthlyHours else knownTargets[knownTargets.size / 2]
        people.forEach { p ->
            val target = p.profile.target.hours
            p.targetMinutes = target?.times(60)
            p.capMinutes = target?.let { ((it + o.maxHoursOverTarget) * 60).roundToInt() }
            p.referenceMinutes = (target?.takeIf { it > 0 } ?: reference).coerceAtLeast(1.0) * 60
            val constraints = p.profile.constraints
            val personalRest = constraints.filterIsInstance<StaffConstraint.MinRestHours>().maxOfOrNull { it.hours } ?: 0
            p.restRules = LabourCode.restRulesApply(p.profile.contract, o.restRulesForAllContracts)
            p.restMinutes = maxOf(o.restHoursFor(p.profile.contract), personalRest) * 60L
            p.maxConsecutive = (constraints.filterIsInstance<StaffConstraint.MaxConsecutiveDays>().map { it.days } + o.maxConsecutiveDays).min()
            p.staticConstraints = constraints.filter { it.isStatic() }
            p.dynamicConstraints = constraints.filter {
                it is StaffConstraint.MaxDaysPerWeek || it is StaffConstraint.MaxHoursPerWeek ||
                    it is StaffConstraint.MaxHoursPerMonth || it is StaffConstraint.MaxDaysPerMonth
            }
        }
    }

    private fun StaffConstraint.isStatic() = this is StaffConstraint.StartBetween || this is StaffConstraint.EndBy ||
        this is StaffConstraint.NotOnDaysOfWeek || this is StaffConstraint.OnlyOnDaysOfWeek || this is StaffConstraint.NotOnDates

    /** Clusters: shifts linked by any overlap (supervision sweep). Crews: rule 5's blocks. */
    private fun buildGroups() {
        // Clusters: classic interval merge over the start-sorted slots.
        var current = ArrayList<Int>()
        var currentEnd = Long.MIN_VALUE
        val clusterLists = ArrayList<List<Int>>()
        slots.forEach { s ->
            if (current.isNotEmpty() && s.start >= currentEnd) {
                clusterLists += current
                current = ArrayList()
            }
            current += s.index
            currentEnd = if (current.size == 1) s.end else maxOf(currentEnd, s.end)
        }
        if (current.isNotEmpty()) clusterLists += current
        clusterLists.forEachIndexed { ci, members -> members.forEach { slots[it].cluster = ci } }
        clusterLists.forEach { clusters += Group(it.toIntArray()) }

        // Crews: same day, overlapping by at least blockMinOverlap of the shorter shift (union-find).
        val parent = IntArray(slots.size) { it }
        fun find(x: Int): Int {
            var r = x
            while (parent[r] != r) r = parent[r]
            var c = x
            while (parent[c] != r) { val next = parent[c]; parent[c] = r; c = next }
            return r
        }
        slots.groupBy { it.shift.date }.values.forEach { day ->
            for (i in day.indices) for (j in i + 1 until day.size) {
                val a = day[i]
                val b = day[j]
                val overlap = minOf(a.end, b.end) - maxOf(a.start, b.start)
                if (overlap > 0 && overlap >= o.blockMinOverlap * minOf(a.minutes, b.minutes)) {
                    parent[find(a.index)] = find(b.index)
                }
            }
        }
        val crewOfRoot = HashMap<Int, Int>()
        val crewLists = ArrayList<ArrayList<Int>>()
        slots.forEach { s ->
            val id = crewOfRoot.getOrPut(find(s.index)) { crewLists += ArrayList<Int>(); crewLists.size - 1 }
            crewLists[id] += s.index
            s.crew = id
        }
        crewLists.forEach { crews += Group(it.toIntArray()) }

        (crews + clusters).forEach { g ->
            g.fillSlots = g.slots.filter { slots[it].toFill }.toIntArray()
            g.open = g.fillSlots.size
        }
    }

    private fun computeStatic() {
        fillSlots.forEach { s ->
            people.forEach { p ->
                val k = s.index * n + p.index
                val preferredSlot = p.profile.availability[s.shift.date]?.preferredSlotId?.let { slotsById[it] }
                if (preferredSlot != null && o.preferenceMode != PreferenceMode.IGNORE) {
                    preferenceFit[k] = if (o.slotMatcher.matches(s.startOfDay, s.endOfDay, preferredSlot)) 1 else -1
                }
                staticRejections[k] = staticCheck(p, s, preferenceFit[k].toInt())
            }
        }
    }

    private fun staticCheck(p: Person, s: Slot, fit: Int): Rejection? {
        val shift = s.shift
        val profile = p.profile
        val role = shift.requiredRole
        if (role != null && role.isNotBlank() && profile.role != role) return simple.getValue(ScheduleReason.NOT_QUALIFIED)
        if (!profile.skills.containsAll(shift.requiredSkills)) return simple.getValue(ScheduleReason.NOT_QUALIFIED)
        if (profile.target.hours == 0.0) return simple.getValue(ScheduleReason.NO_TARGET_HOURS)
        if (shift.date in profile.absentDates) return simple.getValue(ScheduleReason.ABSENT)
        if (profile.staffChecksValidThrough?.let { shift.date > it } == true) return simple.getValue(ScheduleReason.STAFF_CHECK_EXPIRED)
        val day = profile.availability[shift.date]
        if (day?.status == AvailabilityStatus.UNAVAILABLE) return simple.getValue(ScheduleReason.UNAVAILABLE)
        if (day == null && o.requireSubmittedAvailability && !o.assumesAvailable(profile)) {
            return simple.getValue(ScheduleReason.NO_AVAILABILITY)
        }
        if (fit < 0 && o.preferenceMode == PreferenceMode.STRICT) return simple.getValue(ScheduleReason.OUTSIDE_PREFERRED_SLOT)
        p.staticConstraints.forEach { c ->
            val broken = when (c) {
                is StaffConstraint.StartBetween -> s.startOfDay < clockMinutes(c.earliest) || s.startOfDay >= clockMinutes(c.latestExclusive)
                is StaffConstraint.EndBy -> s.endOfDay > clockMinutes(c.latestEnd)
                is StaffConstraint.NotOnDaysOfWeek -> isoDayOfWeek(s.dayEpoch) in c.isoDays
                is StaffConstraint.OnlyOnDaysOfWeek -> isoDayOfWeek(s.dayEpoch) !in c.isoDays
                is StaffConstraint.NotOnDates -> shift.date in c.dates
                else -> false
            }
            if (broken) return Rejection(ScheduleReason.PERSONAL_CONSTRAINT, c)
        }
        return null
    }

    /** Rule 2: fewest available hours first; equal ones by available / still-needed hours, highest first. */
    private fun rankPeople() {
        people.forEach { p ->
            val bestPerDay = HashMap<Long, Int>()
            fillSlots.forEach { s ->
                if (staticRejections[s.index * n + p.index] == null) {
                    bestPerDay[s.dayEpoch] = maxOf(bestPerDay[s.dayEpoch] ?: 0, s.minutes)
                }
            }
            p.availableMinutes = bestPerDay.values.sum()
            val target = p.targetMinutes
            p.commitment = when {
                target == null -> 1.0
                target - p.monthMinutes <= 0 -> 0.0
                else -> p.availableMinutes / (target - p.monthMinutes)
            }
        }
        people.sortedWith(
            compareBy<Person> { it.availableMinutes }
                .thenByDescending { it.commitment }
                .thenBy { tieBreak(it.id, "rank") }
                .thenBy { it.index }
        ).forEachIndexed { rank, p -> p.rank = rank }
    }

    // --- State ----------------------------------------------------------------------------------

    private fun occupy(s: Slot, who: Int) {
        s.assignee = who
        val crew = crews[s.crew]
        val cluster = clusters[s.cluster]
        crew.assigned++
        cluster.assigned++
        val p = if (who >= 0) people[who] else null
        if (p?.lead == true) crew.leads++ else crew.nonLeads++
        if (p?.lead == true) leadsByDate[s.shift.date] = (leadsByDate[s.shift.date] ?: 0) + 1
        if (s.toFill) { crew.open--; cluster.open-- }
        if (p != null) {
            addInterval(p, Interval(s.start, s.end, s.day, true, s.index))
            if (s.day in 0 until windowDays) {
                p.dayAll[s.day]++
                p.dayHere[s.day]++
                p.minutesHere[s.day] += s.minutes
            }
            if (s.shift.date.startsWith(monthPrefix)) p.monthMinutes += s.minutes
        }
    }

    private fun vacate(s: Slot) {
        val who = s.assignee
        val crew = crews[s.crew]
        val cluster = clusters[s.cluster]
        crew.assigned--
        cluster.assigned--
        val p = if (who >= 0) people[who] else null
        if (p?.lead == true) crew.leads-- else crew.nonLeads--
        if (p?.lead == true) leadsByDate[s.shift.date] = (leadsByDate[s.shift.date] ?: 1) - 1
        if (s.toFill) { crew.open++; cluster.open++ }
        if (p != null) {
            p.intervals.removeAll { it.slot == s.index }
            if (s.day in 0 until windowDays) {
                p.dayAll[s.day]--
                p.dayHere[s.day]--
                p.minutesHere[s.day] -= s.minutes
            }
            if (s.shift.date.startsWith(monthPrefix)) p.monthMinutes -= s.minutes
        }
        s.assignee = UNASSIGNED
    }

    private fun assign(p: Person, s: Slot, step: AssignmentReason) {
        val reasons = mutableListOf(step)
        score(p, s, reasons)
        occupy(s, p.index)
        s.reasons = reasons.distinct()
        invalidate(p, s)
    }

    private fun addInterval(p: Person, interval: Interval) {
        var i = p.intervals.size
        while (i > 0 && p.intervals[i - 1].start > interval.start) i--
        p.intervals.add(i, interval)
    }

    /** After [p] took [s]: p's checks everywhere, and everyone's in the shifts around [s]. */
    private fun invalidate(p: Person, s: Slot) {
        fillSlots.forEach { cacheValid[it.index * n + p.index] = false }
        clusters[s.cluster].fillSlots.forEach { si -> for (q in 0 until n) cacheValid[si * n + q] = false }
        // SH-133: a lead on the day lets others take the day's other shifts.
        if (o.requireLeadEachDay && p.lead) {
            fillSlots.forEach { other -> if (other.shift.date == s.shift.date) for (q in 0 until n) cacheValid[other.index * n + q] = false }
        }
    }

    private fun rejection(p: Person, s: Slot): Rejection? {
        val k = s.index * n + p.index
        if (!cacheValid[k]) {
            cache[k] = check(p, s)
            cacheValid[k] = true
        }
        return cache[k]
    }

    // --- The hard rules ---------------------------------------------------------------------

    /**
     * Why [p] cannot take [s] (null: they can). When [s] is held by someone else, this asks
     * whether [p] could take it over (the balancing pass).
     */
    private fun check(p: Person, s: Slot): Rejection? {
        staticRejections[s.index * n + p.index]?.let { return it }
        for (iv in p.intervals) {
            if (iv.slot == s.index) continue
            if (iv.start < s.end && s.start < iv.end) {
                return simple.getValue(if (iv.here) ScheduleReason.OVERLAPS_OWN_SHIFT else ScheduleReason.BUSY_ELSEWHERE)
            }
        }
        if (s.day in 0 until windowDays && p.dayAll[s.day] >= o.maxShiftsPerDay) return simple.getValue(ScheduleReason.ALREADY_WORKING_THAT_DAY)
        for (iv in p.intervals) {
            val gap = if (iv.end <= s.start) s.start - iv.end else iv.start - s.end
            if (gap < p.restMinutes) return simple.getValue(ScheduleReason.INSUFFICIENT_REST)
        }
        val span = MinuteSpan(s.start, s.end)
        val others = p.intervals.filter { it.slot != s.index }.map { MinuteSpan(it.start, it.end) }
        // SH-112: the labour code's 11 hours in each 24 (also a shift longer than 13 hours).
        if (p.restRules) {
            LabourCode.dailyRestMinutesWith(others, span)?.let {
                if (it < LabourCode.DAILY_REST_HOURS * 60L) return simple.getValue(ScheduleReason.INSUFFICIENT_REST)
            }
        }
        if (runLength(p, s.day) > p.maxConsecutive) return simple.getValue(ScheduleReason.TOO_MANY_CONSECUTIVE_DAYS)
        // SH-112: 35 uninterrupted hours in each week the shift falls in (weeks from the month's 1st).
        val withIt = others + span
        if (p.restRules && LabourCode.weeksOf(span, monthStartEpoch).any { LabourCode.weeklyRestMinutes(withIt, it) < LabourCode.WEEKLY_REST_HOURS * 60L }) {
            return simple.getValue(ScheduleReason.NO_WEEKLY_REST)
        }
        val inMonth = s.shift.date.startsWith(monthPrefix)
        val added = if (inMonth) s.minutes else 0
        if (p.hoursRules && inMonth && breaksHoursRules(p, s)) return simple.getValue(ScheduleReason.LABOUR_HOURS_LIMIT)
        p.capMinutes?.let { if (p.monthMinutes + added > it) return simple.getValue(ScheduleReason.TARGET_REACHED) }
        p.dynamicConstraints.forEach { c ->
            val broken = when (c) {
                is StaffConstraint.MaxHoursPerMonth -> p.monthMinutes + added > c.hours * 60 + 1e-6
                is StaffConstraint.MaxDaysPerMonth -> inMonth && daysHereInMonth(p) + newDay(p, s) > c.days
                is StaffConstraint.MaxDaysPerWeek -> weekDays(p, s.day) + newDay(p, s) > c.days
                is StaffConstraint.MaxHoursPerWeek -> weekMinutes(p, s.day) + s.minutes > c.hours * 60 + 1e-6
                else -> false
            }
            if (broken) return Rejection(ScheduleReason.PERSONAL_CONSTRAINT, c)
        }
        crewRejection(p, s)?.let { return it }
        if (o.requireLeadEachDay && !p.lead && s.shift.date in leadPossible && dayLeadsWithout(s) == 0) {
            return simple.getValue(ScheduleReason.NEEDS_SHIFT_LEAD)
        }
        if (p.supervised) supervisionRejection(p, s)?.let { return it }
        return null
    }

    /** SH-133: the leads working [s]'s day here, not counting whoever holds [s] now. */
    private fun dayLeadsWithout(s: Slot): Int {
        val holder = s.assignee.takeIf { it >= 0 }?.let { people[it] }
        return (leadsByDate[s.shift.date] ?: 0) - if (holder?.lead == true) 1 else 0
    }

    /** Rule 5, on the block as it would be with [p] in [s] (replacing whoever holds it). */
    private fun crewRejection(p: Person, s: Slot): Rejection? {
        val crew = crews[s.crew]
        if (crew.slots.size < 2) return null
        var leads = crew.leads
        var nonLeads = crew.nonLeads
        var assigned = crew.assigned
        when {
            s.assignee >= 0 -> if (people[s.assignee].lead) leads-- else nonLeads--
            s.assignee == UNKNOWN_PERSON -> nonLeads--
            else -> assigned++
        }
        if (p.lead) leads++ else nonLeads++
        if (o.requireShiftLead && leads == 0) return simple.getValue(ScheduleReason.NEEDS_SHIFT_LEAD)
        if (o.requireNonLead && assigned >= 2 && nonLeads == 0) return simple.getValue(ScheduleReason.NEEDS_NON_LEAD)
        return null
    }

    /**
     * Rule 7 for [p], who needs supervision, in [s] (replacing whoever holds it): someone who does
     * not need supervision works with them all the time. The same check as the roster's day
     * warnings ([SupervisionCheck.uncoveredWindows]), over the shifts overlapping [s]'s cluster.
     */
    private fun supervisionRejection(p: Person, s: Slot): Rejection? {
        val cluster = clusters[s.cluster]
        val spans = ArrayList<WorkSpan>(cluster.slots.size)
        spans += WorkSpan(p.id, s.start, s.end)
        for (si in cluster.slots) {
            if (si == s.index) continue
            val other = slots[si]
            when {
                other.assignee >= 0 -> spans += WorkSpan(people[other.assignee].id, other.start, other.end)
                // Someone not on the team (left, inactive): present, nothing else known.
                other.assignee == UNKNOWN_PERSON -> spans += WorkSpan(other.shift.userId.orEmpty(), other.start, other.end)
            }
        }
        val uncovered = SupervisionCheck.uncoveredWindows(spans) { uid -> personIndex[uid]?.let { people[it].supervised } ?: false }
        return if (uncovered.any { it.start < s.end && it.end > s.start }) simple.getValue(ScheduleReason.NEEDS_SUPERVISION) else null
    }

    /**
     * SH-112, employment contracts: the day above the daily norm (overtime is never planned), the
     * month above 48 hours a week on average, or its overtime above the yearly cap's monthly share.
     */
    private fun breaksHoursRules(p: Person, s: Slot): Boolean {
        val norm = LabourCode.DAILY_NORM_HOURS * 60
        val day = s.day.takeIf { it in 0 until windowDays } ?: return false
        if (p.minutesHere[day] + s.minutes > norm + 1e-6) return true
        if (p.monthMinutes + s.minutes > LabourCode.periodMaxHours(monthDays.count()) * 60 + 1e-6) return true
        val perDay = monthDays.map { if (it == day) p.minutesHere[it] + s.minutes else p.minutesHere[it] }
        val periodNorm = p.targetMinutes?.takeIf { it > 0 } ?: (problem.nominalMonthlyHours * 60)
        return LabourCode.overtimeMinutes(perDay, norm, periodNorm) > LabourCode.MONTHLY_OVERTIME_HOURS * 60 + 1e-6
    }

    private fun runLength(p: Person, day: Int): Int {
        if (day !in 0 until windowDays) return 1
        var left = 0
        var i = day - 1
        while (i >= 0 && p.dayAll[i] > 0) { left++; i-- }
        var right = 0
        i = day + 1
        while (i < windowDays && p.dayAll[i] > 0) { right++; i++ }
        return left + 1 + right
    }

    private fun newDay(p: Person, s: Slot) = if (s.day in 0 until windowDays && p.dayHere[s.day] > 0) 0 else 1

    private fun daysHereInMonth(p: Person): Int = monthDays.count { p.dayHere[it] > 0 }

    private fun weekStart(day: Int): Int {
        val epoch = base + day
        return day - (epoch + 3 - (o.firstDayOfWeek - 1)).mod(7L).toInt()
    }

    private fun weekDays(p: Person, day: Int): Int {
        val start = weekStart(day)
        return (start until start + 7).count { it in 0 until windowDays && p.dayHere[it] > 0 }
    }

    private fun weekMinutes(p: Person, day: Int): Int {
        val start = weekStart(day)
        return (start until start + 7).sumOf { if (it in 0 until windowDays) p.minutesHere[it] else 0 }
    }

    // --- The soft rules -------------------------------------------------------------------------

    private fun score(p: Person, s: Slot, reasons: MutableList<AssignmentReason>? = null): Double {
        var score = 0.0
        val crew = crews[s.crew]
        if (crew.slots.size >= 2) {
            if (p.lead) {
                if (crew.leads == 0) score += w.shiftLeadNeeded else score -= w.spareLead
            } else if (crew.nonLeads == 0 && crew.assigned > 0) {
                score += w.nonLeadNeeded
            }
        }
        if (p.owner && o.ownerTakesMornings) {
            score += if (s.startOfDay < morningEnd) w.ownerMorning else -w.ownerMorning / 2
        }
        val scarcity = if (n <= 1) 1.0 else 1.0 - p.rank.toDouble() / (n - 1)
        score += w.scarcity * scarcity
        if (scarcity >= 2.0 / 3) reasons?.add(AssignmentReason.SCARCE_AVAILABILITY)

        val ratio = p.monthMinutes / p.referenceMinutes
        score += w.belowTarget * (1 - min(ratio, 1.5) / 1.5)
        if (p.targetMinutes != null && ratio < 0.5) reasons?.add(AssignmentReason.BELOW_TARGET)

        if (s.day in 0 until windowDays) {
            val run = runLength(p, s.day)
            if (run > 1) {
                if (run <= o.preferredRunDays.last) {
                    score += w.extendsRun
                    reasons?.add(AssignmentReason.EXTENDS_RUN)
                } else {
                    score -= w.overlongRun * (run - o.preferredRunDays.last)
                }
            }
            var idle = 0
            var i = s.day - 1
            while (i >= 0 && p.dayAll[i] == 0) { idle++; i-- }
            if (idle > o.maxIdleDays) {
                score += w.longBreak * min(1.0, (idle - o.maxIdleDays) / 3.0)
                reasons?.add(AssignmentReason.AFTER_LONG_BREAK)
            }
        }
        when (preferenceFit[s.index * n + p.index].toInt()) {
            1 -> { score += w.preferredSlot; reasons?.add(AssignmentReason.PREFERRED_SLOT) }
            -1 -> score -= w.preferredSlot
        }
        return score + w.tieBreak * tieBreak(p.id, s.shift.id)
    }

    /** A stable pseudo-random number in [0, 1) from the seed and two keys (FNV-1a). */
    private fun tieBreak(a: String, b: String): Double {
        var h = -0x340d631b7bdddcdbL xor o.seed
        fun mix(text: String) {
            for (ch in text) { h = h xor ch.code.toLong(); h *= 0x100000001b3L }
            h = h xor 0xff; h *= 0x100000001b3L
        }
        mix(a)
        mix(b)
        return ((h ushr 11).toDouble()) / (1L shl 53).toDouble()
    }

    // --- The steps ------------------------------------------------------------------------------

    /**
     * SH-90: the outside proposals that pass every hard rule ([check]), repeated while any gets
     * placed; the rest are [dropped] with the reason.
     */
    private fun placeProposals() {
        val slotById = slots.associateBy { it.shift.id }
        val seen = HashSet<String>()
        var pending = ArrayList<Pair<Slot, ProposedAssignment>>()
        problem.proposals.forEach { proposal ->
            val slot = slotById[proposal.shiftId]
            when {
                slot == null || !slot.toFill -> dropped += DroppedProposal(proposal.shiftId, proposal.userId, DropReason.SHIFT_NOT_OPEN)
                !seen.add(proposal.shiftId) -> dropped += DroppedProposal(proposal.shiftId, proposal.userId, DropReason.DUPLICATE)
                personIndex[proposal.userId] == null -> dropped += DroppedProposal(proposal.shiftId, proposal.userId, DropReason.UNKNOWN_PERSON)
                else -> pending += slot to proposal
            }
        }
        var placed = true
        while (placed && pending.isNotEmpty()) {
            placed = false
            val next = ArrayList<Pair<Slot, ProposedAssignment>>()
            pending.forEach { (slot, proposal) ->
                val person = people[personIndex.getValue(proposal.userId)]
                if (rejection(person, slot) == null) {
                    assign(person, slot, proposal.reason ?: AssignmentReason.BEST_FIT)
                    // The proposal's own reason only: the rules' scoring did not choose it.
                    slot.reasons = listOf(proposal.reason ?: AssignmentReason.BEST_FIT)
                    slot.proposed = true
                    placed = true
                } else {
                    next += slot to proposal
                }
            }
            pending = next
        }
        pending.forEach { (slot, proposal) ->
            val rejection = check(people[personIndex.getValue(proposal.userId)], slot)
            dropped += DroppedProposal(proposal.shiftId, proposal.userId, DropReason.BREAKS_RULE, rejection?.reason, rejection?.constraint)
        }
    }

    /** Rule 6: each day, the owner's earliest morning shift they can take. */
    private fun ownerMornings() {
        val mornings = fillSlots.filter { it.startOfDay < morningEnd }
            .groupBy { it.dayEpoch }.entries.sortedBy { it.key }
            .map { (_, day) -> day.sortedWith(compareBy({ it.startOfDay }, { it.index })) }
        people.filter { it.owner }.forEach { owner ->
            mornings.forEach { day ->
                day.firstOrNull { it.assignee == UNASSIGNED && rejection(owner, it) == null }
                    ?.let { assign(owner, it, AssignmentReason.OWNER_MORNING) }
            }
        }
    }

    /**
     * Rule 5: give each block without a lead ([lead]) one, or each block with only leads someone
     * who is not one; the block with the fewest possibilities first.
     */
    private fun crewPhase(lead: Boolean) {
        while (true) {
            var best: Group? = null
            var bestPairs = Int.MAX_VALUE
            for (crew in crews) {
                if (crew.slots.size < 2 || crew.open == 0) continue
                if (lead && crew.leads > 0) continue
                if (!lead && (crew.assigned == 0 || crew.nonLeads > 0)) continue
                var pairs = 0
                for (si in crew.fillSlots) {
                    val s = slots[si]
                    if (s.assignee != UNASSIGNED) continue
                    for (p in people) if (p.lead == lead && rejection(p, s) == null) pairs++
                }
                if (pairs in 1 until bestPairs) { best = crew; bestPairs = pairs }
            }
            val crew = best ?: return
            var bestPerson: Person? = null
            var bestSlot: Slot? = null
            var bestScore = Double.NEGATIVE_INFINITY
            for (si in crew.fillSlots) {
                val s = slots[si]
                if (s.assignee != UNASSIGNED) continue
                for (p in people) {
                    if (p.lead != lead || rejection(p, s) != null) continue
                    val score = score(p, s)
                    if (score > bestScore) { bestScore = score; bestPerson = p; bestSlot = s }
                }
            }
            assign(bestPerson!!, bestSlot!!, if (lead) AssignmentReason.SHIFT_LEAD_NEEDED else AssignmentReason.NON_LEAD_NEEDED)
        }
    }

    /** The rest: the open shift with the fewest candidates first, to the best of them. */
    private fun fill() {
        while (true) {
            var best: Slot? = null
            var bestCount = Int.MAX_VALUE
            for (s in fillSlots) {
                if (s.assignee != UNASSIGNED) continue
                var count = 0
                for (p in people) if (rejection(p, s) == null) count++
                if (count in 1 until bestCount) {
                    best = s
                    bestCount = count
                    if (count == 1) break
                }
            }
            val slot = best ?: return
            val person = people.filter { rejection(it, slot) == null }.maxBy { score(it, slot) }
            assign(person, slot, AssignmentReason.BEST_FIT)
        }
    }

    /** Their optymalizujGrafikAlgorytmem: even out hours/target among civil-law contracts. */
    private fun balance(): BalancingSummary {
        val eligible = people.filter {
            it.profile.contract == SystemContractType.CIVIL_LAW_CONTRACT && (it.targetMinutes ?: 0.0) > 0 && !it.owner
        }
        fun ratio(p: Person) = p.monthMinutes / p.targetMinutes!!
        fun spread() = if (eligible.size < 2) 0.0 else eligible.maxOf { ratio(it) } - eligible.minOf { ratio(it) }
        val before = spread()
        val moves = mutableListOf<BalancingMove>()
        if (!o.balanceCivilLaw || eligible.size < 2) {
            return BalancingSummary(moves, 0, before, before, before < o.balanceTolerance)
        }
        var iterations = 0
        while (iterations < o.balanceMaxIterations && spread() >= o.balanceTolerance) {
            val move = findMove(eligible.sortedWith(compareByDescending<Person> { ratio(it) }.thenBy { it.index }), ::ratio) ?: break
            val (slot, donor, receiver) = move
            vacate(slot)
            invalidate(donor, slot)
            occupy(slot, receiver.index)
            slot.reasons = listOf(AssignmentReason.BALANCED_HOURS)
            if (slot.balancedFrom == UNASSIGNED) slot.balancedFrom = donor.index
            invalidate(receiver, slot)
            moves += BalancingMove(slot.shift.id, donor.id, receiver.id, slot.minutes / 60.0)
            iterations++
        }
        val after = spread()
        return BalancingSummary(moves, iterations, before, after, after < o.balanceTolerance)
    }

    private fun findMove(byRatioDesc: List<Person>, ratio: (Person) -> Double): Triple<Slot, Person, Person>? {
        val ascending = byRatioDesc.reversed()
        for (donor in byRatioDesc) {
            val donorShifts = fillSlots.filter { it.assignee == donor.index && !it.proposed }
            if (donorShifts.isEmpty()) continue
            for (receiver in ascending) {
                if (receiver === donor) continue
                val gap = ratio(donor) - ratio(receiver)
                if (gap < o.balanceTolerance) break
                var best: Slot? = null
                var bestScore = Double.NEGATIVE_INFINITY
                for (s in donorShifts) {
                    if (preferenceFit[s.index * n + receiver.index] < 0) continue
                    val donorAfter = (donor.monthMinutes - s.minutes) / donor.targetMinutes!!
                    val receiverAfter = (receiver.monthMinutes + s.minutes) / receiver.targetMinutes!!
                    if (abs(donorAfter - receiverAfter) >= gap) continue
                    if (check(receiver, s) != null) continue
                    val score = score(receiver, s)
                    if (score > bestScore) { bestScore = score; best = s }
                }
                if (best != null) return Triple(best, donor, receiver)
            }
        }
        return null
    }

    // --- The plan -------------------------------------------------------------------------------

    private fun result(balancing: BalancingSummary): SchedulePlan {
        val initialId = { s: Slot -> if (s.initialAssignee >= 0) people[s.initialAssignee].id else s.shift.userId }
        val assignments = fillSlots.filter { it.assignee >= 0 && it.assignee != it.initialAssignee }.map { s ->
            val p = people[s.assignee]
            PlannedAssignment(
                shiftId = s.shift.id, date = s.shift.date, plannedTime = s.shift.plannedTime, hours = s.minutes / 60.0,
                userId = p.id, displayName = p.profile.displayName, previousUserId = initialId(s),
                reasons = s.reasons, balancedFromUserId = s.balancedFrom.takeIf { it >= 0 }?.let { people[it].id },
                source = if (s.proposed) AssignmentSource.AI else AssignmentSource.RULES
            )
        }
        val unfilled = fillSlots.filter { it.assignee == UNASSIGNED }.map { s ->
            val rejections = people.mapNotNull { p ->
                check(p, s)?.let { CandidateRejection(p.id, p.profile.displayName, it.reason, it.constraint) }
            }
            val reason = if (n == 0) ScheduleReason.NO_STAFF else rejections.groupingBy { it.reason }.eachCount()
                .entries.sortedWith(compareByDescending<Map.Entry<ScheduleReason, Int>> { it.value }.thenByDescending { it.key.ordinal })
                .firstOrNull()?.key ?: ScheduleReason.NO_STAFF
            UnfilledShift(s.shift.id, s.shift.date, s.shift.plannedTime, s.shift.requiredRole, reason, rejections, initialId(s))
        }
        val staff = people.map { p ->
            val newShifts = fillSlots.count { it.assignee == p.index && it.initialAssignee != p.index }
            val days = daysHereInMonth(p)
            var longest = 0
            var run = 0
            p.dayAll.forEach { if (it > 0) { run++; longest = maxOf(longest, run) } else run = 0 }
            StaffSummary(
                userId = p.id, displayName = p.profile.displayName, contract = p.profile.contract,
                isOwner = p.owner, leadsShifts = p.lead, needsSupervision = p.supervised, target = p.profile.target,
                hoursBefore = p.monthMinutesBefore / 60.0, hoursAfter = p.monthMinutes / 60.0, newShifts = newShifts,
                daysWorked = days, longestStreak = longest, availableHours = p.availableMinutes / 60.0, priorityRank = p.rank,
                targetRatio = p.targetMinutes?.takeIf { it > 0 }?.let { p.monthMinutes / it }
            )
        }
        return SchedulePlan(
            rosterId = problem.rosterId, range = problem.range, options = o,
            assignments = assignments, unfilled = unfilled, staff = staff,
            priorityOrder = people.sortedBy { it.rank }.map { it.id },
            balancing = balancing, warnings = warnings(), shiftsToFill = fillSlots.size,
            dropped = dropped
        )
    }

    private fun warnings(): List<ScheduleWarning> = buildList {
        if (o.requireSubmittedAvailability) {
            people.filter { !it.profile.availabilitySubmitted }.map { it.id }.takeIf { it.isNotEmpty() }
                ?.let {
                    val kind = if (o.firstRoster) WarningKind.FIRST_ROSTER_WITHOUT_AVAILABILITY else WarningKind.NO_AVAILABILITY_SUBMITTED
                    add(ScheduleWarning(kind, userIds = it))
                }
        }
        if (o.requireShiftLead && people.none { it.lead } && crews.any { it.slots.size >= 2 && it.fillSlots.isNotEmpty() }) {
            add(ScheduleWarning(WarningKind.NO_SHIFT_LEADS))
        }
        // Blocks the plan could not fix because what was kept already breaks rule 5 or 7.
        crews.filter { it.slots.size >= 2 && it.assigned > 0 }.forEach { crew ->
            val ids = crew.slots.filter { slots[it].assignee != UNASSIGNED }.map { slots[it].shift.id }
            if (o.requireShiftLead && crew.leads == 0) {
                add(ScheduleWarning(WarningKind.KEPT_ASSIGNMENT_BREAKS_RULE, shiftIds = ids, reason = ScheduleReason.NEEDS_SHIFT_LEAD))
            } else if (o.requireNonLead && crew.assigned >= 2 && crew.nonLeads == 0) {
                add(ScheduleWarning(WarningKind.KEPT_ASSIGNMENT_BREAKS_RULE, shiftIds = ids, reason = ScheduleReason.NEEDS_NON_LEAD))
            }
        }
        // SH-133: days filled without a lead because none could work them.
        if (o.requireLeadEachDay) {
            fillSlots.filter { it.assignee >= 0 && (leadsByDate[it.shift.date] ?: 0) == 0 }.map { it.shift.id }.takeIf { it.isNotEmpty() }
                ?.let { add(ScheduleWarning(WarningKind.DAYS_WITHOUT_LEAD, shiftIds = it, reason = ScheduleReason.NEEDS_SHIFT_LEAD)) }
        }
        slots.filter { s -> s.assignee >= 0 && people[s.assignee].supervised && supervisionRejection(people[s.assignee], s) != null }
            .map { it.shift.id }.takeIf { it.isNotEmpty() }
            ?.let { add(ScheduleWarning(WarningKind.KEPT_ASSIGNMENT_BREAKS_RULE, shiftIds = it, reason = ScheduleReason.NEEDS_SUPERVISION)) }
    }

    // --- Time -----------------------------------------------------------------------------------

    private fun parseTimes(shift: Shift): Triple<Long, Long, Long>? {
        val start = isoMinutes(shift.plannedTime.start) ?: return null
        val end = isoMinutes(shift.plannedTime.end) ?: return null
        if (end <= start) return null
        val day = runCatching { epochDay(shift.date) }.getOrNull() ?: return null
        return Triple(start, end, day)
    }
}

internal fun epochDay(isoDate: String): Long = LocalDate.parse(isoDate.substring(0, 10)).toEpochDays()

/** Minutes since the epoch of a local "YYYY-MM-DDTHH:mm[:ss]" timestamp; null when unreadable. */
internal fun isoMinutes(iso: String): Long? = runCatching {
    epochDay(iso) * 1440 + clockMinutes(iso.substring(11, 16))
}.getOrNull()

/** ISO day of week (1 = Monday) of an epoch day (day 0, 1970-01-01, was a Thursday). */
private fun isoDayOfWeek(epochDay: Long): Int = (epochDay + 3).mod(7L).toInt() + 1
